import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, PieChartIcon, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useOutcomeReasons } from "@/hooks/use-population-insights";
import { useInsightsContext } from "@/contexts/insights-context";
import { useState, useMemo } from "react";
import { useToggleableSeries, InteractiveLegend, type LegendSeries } from "./interactive-legend";

type SpeciesFilter = "all" | "dogs" | "cats" | "other";

const SPECIES_API_MAP: Record<SpeciesFilter, string | undefined> = {
  all: undefined,
  dogs: "DOG",
  cats: "CAT",
  other: undefined,
};

const REASON_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(210 70% 50%)",
  "hsl(280 65% 55%)",
  "hsl(160 60% 45%)",
];

interface OutcomeReasonChartProps {
  days: number;
}

function StatCard({ label, value, testId }: { label: string; value: string | number; testId: string }) {
  return (
    <div className="flex flex-col p-3 rounded-md border" data-testid={testId}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold">{value}</span>
    </div>
  );
}

export function OutcomeReasonChart({ days }: OutcomeReasonChartProps) {
  const { selectedShelter, isSystemView } = useInsightsContext();
  const location = isSystemView ? undefined : selectedShelter;
  const [speciesFilter, setSpeciesFilter] = useState<SpeciesFilter>("all");

  const apiSpecies = SPECIES_API_MAP[speciesFilter];
  const { data, isLoading, isError, refetch } = useOutcomeReasons(days, location, apiSpecies);

  const displayData = useMemo(() => {
    if (!data) return null;
    if (speciesFilter === "all") {
      return { total: data.total, reasons: data.reasons };
    }
    if (speciesFilter === "other" && data.bySpecies?.OTHER) {
      return data.bySpecies.OTHER;
    }
    return { total: data.total, reasons: data.reasons };
  }, [data, speciesFilter]);

  const chartData = useMemo(() => {
    if (!displayData?.reasons) return [];
    return displayData.reasons.map((r, i) => ({
      name: r.reason,
      value: r.count,
      percentage: r.percentage,
      fill: REASON_COLORS[i % REASON_COLORS.length],
    }));
  }, [displayData]);

  const legendSeries = useMemo<LegendSeries[]>(() =>
    chartData.map(d => ({ key: d.name, label: d.name, color: d.fill })),
    [chartData]
  );

  const { visibleSeries, toggleSeries } = useToggleableSeries(legendSeries);

  const filteredChartData = useMemo(
    () => chartData.filter(d => visibleSeries.has(d.name)),
    [chartData, visibleSeries]
  );

  return (
    <Card data-testid="card-outcome-reasons">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <PieChartIcon className="h-5 w-5 text-muted-foreground" />
            Outcome Reasons
          </CardTitle>
        </div>
        <Select value={speciesFilter} onValueChange={(v) => setSpeciesFilter(v as SpeciesFilter)}>
          <SelectTrigger className="w-[140px]" data-testid="select-species-outcome-reasons">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Species</SelectItem>
            <SelectItem value="dogs">Dogs</SelectItem>
            <SelectItem value="cats">Cats</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="space-y-4" data-testid="loading-outcome-reasons">
            <div className="flex gap-3">
              <Skeleton className="h-16 flex-1" />
            </div>
            <Skeleton className="h-[280px] w-full" />
          </div>
        )}
        {isError && (
          <Alert variant="destructive" data-testid="error-outcome-reasons">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>Failed to load outcome reason data</span>
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-outcome-reasons">
                <RefreshCw className="h-3 w-3 mr-1" /> Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {displayData && displayData.total === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-outcome-reasons">
            No outcome data available yet.
          </div>
        )}
        {displayData && displayData.total > 0 && (
          <>
            <div className="mb-4">
              <p className="text-xs text-muted-foreground mb-1" data-testid="text-outcome-reason-stats-hint">
                All records within selected date range
              </p>
              <div className="flex flex-wrap gap-3">
                <StatCard label="Total Outcomes" value={displayData.total} testId="stat-total-outcome-reasons" />
                {displayData.reasons.slice(0, 3).map((r) => (
                  <StatCard
                    key={r.reason}
                    label={r.reason}
                    value={`${r.count} (${r.percentage}%)`}
                    testId={`stat-reason-${r.reason.toLowerCase().replace(/\s+/g, "-")}`}
                  />
                ))}
              </div>
            </div>
            <InteractiveLegend
              series={legendSeries}
              visibleSeries={visibleSeries}
              onToggle={toggleSeries}
              testIdPrefix="outcome-legend"
            />
            <div className="h-[280px]" data-testid="chart-outcome-reasons">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={filteredChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percentage }: { name: string; percentage: number }) => `${name} (${percentage}%)`}
                    labelLine={{ strokeWidth: 1 }}
                  >
                    {filteredChartData.map((entry, index) => (
                      <Cell key={index} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number, name: string) => [`${value} animals`, name]}
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      borderColor: "hsl(var(--border))",
                      borderRadius: "6px",
                      color: "hsl(var(--card-foreground))",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
